// A fix of one game in the import preview: its division and its start. It
// changes the draft only; nothing is written until the import is applied

import { useFormik } from 'formik';
import * as Yup from 'yup';
import Select from './Select';
import { useModal } from './ModalProvider';
import { time12To24H, time24To12H } from '../utils/misc';
import './ScheduleImportFix.scss';

// Every five minutes from 6 AM: a misread time can be any of them
const starts = Array.from({ length: 18 * 12 }, (_, i) => {
  const minutes = 6 * 60 + i * 5;
  const hh = String(Math.floor(minutes / 60)).padStart(2, '0');
  const mm = String(minutes % 60).padStart(2, '0');
  return time24To12H(`${hh}:${mm}`);
});

export default function ScheduleImportFix({
  game,
  original,
  divisions,
  issues,
  onSave,
}) {
  const { closeModal } = useModal();
  const current = time24To12H(game.start);
  const isFixed =
    game.division !== original.division || game.start !== original.start;

  const formik = useFormik({
    initialValues: { division: game.division, start: current },
    validationSchema: Yup.object({
      division: Yup.string().required('Required'),
      start: Yup.string().required('Required'),
    }),
    onSubmit: ({ division, start }) => {
      const fix = {
        ...(division !== original.division && { division }),
        ...(time12To24H(start) !== original.start && {
          start: time12To24H(start),
        }),
      };
      onSave(Object.keys(fix).length > 0 ? fix : null);
      closeModal();
    },
  });

  const revert = () => {
    onSave(null);
    closeModal();
  };

  return (
    <form className='schedule-import-fix' onSubmit={formik.handleSubmit}>
      <p className='sheet-cells'>
        Cells <span className='font-medium'>{original.cells || '—'}</span>,
        sides{' '}
        <span className='font-medium'>
          {original.side1 || '?'} – {original.side2 || '?'}
        </span>{' '}
        as on the sheet, not saved
      </p>
      {issues.length > 0 && (
        <ul className='issues'>
          {issues.map((issue, i) => (
            <li key={i} className={issue.severity}>
              {issue.message}
            </li>
          ))}
        </ul>
      )}
      <div className='inputs'>
        <Select
          className='division'
          options={divisions}
          value={formik.values.division}
          onChange={(v) => formik.setFieldValue('division', v)}
          placeholder='Division'
          error={formik.errors.division}
        />
        <Select
          className='start'
          options={starts.includes(current) ? starts : [current, ...starts]}
          value={formik.values.start}
          onChange={(v) => formik.setFieldValue('start', v)}
          placeholder='Start'
          error={formik.errors.start}
          search
        />
      </div>
      <div className='actions'>
        {isFixed && (
          <button type='button' className='bw' onClick={revert}>
            As on the sheet
          </button>
        )}
        <button type='submit'>Fix</button>
      </div>
    </form>
  );
}
